import { useState } from "react";
import type { DDFunction } from "@app/portal-sdk";
import { Alert, Box, Button, Stack, TextField, Typography } from "@app/ui-kit";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import { errorMessage } from "../../../lib/useApiQuery";
import { useDd } from "../lib/dd";
import { reviewAreaWords } from "../lib/areaWords";

/**
 * DDP-5: choosing workstreams is a list, not a field of chips. One row per functional area, all the same height, in one
 * column: the label, its one-line description, the review-area count, and a toggle. The library's own order, the
 * client's own beneath under a divider labelled Ours. A selected row is selected by more than colour: a checked
 * checkbox and a heavier border. The same component renders at step 4 of the setup and on the workstreams screen.
 */
export const ROW_HEIGHT = 72;

/**
 * 3.3: one meaning for "review areas", everywhere: the leaves a workstream scopes, the ones that take questions and a
 * rating (HIE-1). The picker used to count the top of the tree (10 for Technology) while the engagement counted the
 * leaves (13); now both count the leaves, and the picker says how many of them are sub-areas.
 */
export const areaWords = (f: DDFunction): string => {
  const leaves = f.leafCount ?? f.reviewAreaCount;
  const sub = f.reviewAreas.reduce((n, a) => n + (a.children?.length ?? 0), 0);
  return reviewAreaWords(leaves, sub);
};

export const WorkstreamList = ({ functions, chosen, editable, busy, onToggle, onAdded }: {
  functions: DDFunction[]; chosen: Set<string>; editable: boolean; busy: boolean;
  onToggle: (f: DDFunction, selected: boolean) => void; onAdded?: () => Promise<void>;
}) => {
  const { canManage } = useDd();
  const [adding, setAdding] = useState(false);
  const library = functions.filter((f) => f.origin === "library");
  const own = functions.filter((f) => f.origin === "own");
  const row = (f: DDFunction) => {
    const selected = chosen.has(f.id);
    return (
      <Box key={f.id} component="label" data-testid={`function-row-${f.code}`} data-selected={selected ? "true" : "false"} data-row-height={ROW_HEIGHT}
        sx={{ display: "grid", gridTemplateColumns: { xs: "auto 1fr", sm: "auto 1fr auto" }, alignItems: "center", columnGap: 2, rowGap: 0.5, minHeight: ROW_HEIGHT, height: { sm: ROW_HEIGHT }, px: 1.5, py: 1, boxSizing: "border-box",
          border: selected ? 2 : 1, borderColor: selected ? "primary.main" : "divider", borderRadius: 1, cursor: editable ? "pointer" : "default", bgcolor: selected ? "action.selected" : "transparent" }}>
        <Checkbox size="small" checked={selected} disabled={!editable || busy} onChange={(e) => onToggle(f, e.target.checked)} inputProps={{ "aria-label": `${f.label}: ${selected ? "chosen" : "not chosen"}` }} />
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>{f.label}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{f.description ?? "No description written."}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: { xs: "2", sm: "auto" }, whiteSpace: "nowrap" }} data-testid={`fn-areas-${f.code}`}>{areaWords(f)}</Typography>
      </Box>
    );
  };
  return (
    <Stack spacing={1} data-testid="workstream-list">
      {library.map(row)}
      {(own.length > 0 || (editable && canManage)) && <Divider textAlign="left" sx={{ my: 1 }}><Typography variant="overline" color="text.secondary" data-testid="ours-divider">Yours</Typography></Divider>}
      <Stack spacing={1} data-testid="ours-group">{own.map(row)}</Stack>
      {editable && canManage && <Box><Button size="small" variant="outlined" onClick={() => setAdding(true)} disabled={busy}>Add a functional area of your own</Button></Box>}
      <AddFunctionDialog open={adding} onClose={() => setAdding(false)} onAdded={async () => { setAdding(false); await onAdded?.(); }} />
    </Stack>
  );
};

const AddFunctionDialog = ({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: () => Promise<void> }) => {
  const { api, base } = useDd();
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add a functional area of your own</DialogTitle>
      <DialogContent><Stack spacing={1.5} sx={{ pt: 1 }}>{error && <Alert severity="error">{error}</Alert>}<Typography variant="body2" color="text.secondary">It is yours, not the library's, and it lands under Yours. It starts with no review areas; add them from the engagement.</Typography><TextField size="small" label="Name" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus /><TextField size="small" label="One-line description" value={description} onChange={(e) => setDescription(e.target.value)} /></Stack></DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}><Button onClick={onClose} disabled={busy}>Cancel</Button><Button variant="contained" disabled={busy || !label.trim()} onClick={() => { setBusy(true); setError(null); void api.request(`${base}/functions`, { method: "POST", json: { label: label.trim(), description: description.trim() || null } }).then(async () => { setLabel(""); setDescription(""); await onAdded(); }).catch((e) => setError(errorMessage(e))).finally(() => setBusy(false)); }}>Add</Button></DialogActions>
    </Dialog>
  );
};
